@if(this.user.isGetPremimum){
<section class="payment-checkout relative min-h-screen overflow-hidden bg-[#fffcf9] px-4 pb-16 pt-12 sm:px-6 sm:pt-16 lg:px-8 lg:py-20">

  <div class="relative mx-auto max-w-5xl">
    <!-- Heading -->
    <header class="animate-fade-in-up mx-auto max-w-2xl text-center">
      <p class="text-xs font-semibold uppercase tracking-[0.18em] text-[#8c52ff]">Confirm your plan</p>
      <h1 class="mt-4 font-sans text-3xl font-semibold tracking-tight text-[#111111] sm:text-4xl">
        Review and complete your payment
      </h1>
      <button type="button" (click)="changePlan()"
              class="group mt-4 inline-flex items-center gap-1.5 text-sm font-medium text-black/50 transition-colors duration-200 hover:text-[#8c52ff] focus:outline-none focus-visible:ring-4 focus-visible:ring-[#8c52ff]/30 rounded-md">
        <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
             class="h-4 w-4 transition-transform duration-200 group-hover:-translate-x-1" aria-hidden="true">
          <path d="M16 10H4M9 5l-5 5 5 5" />
        </svg>
        Change plan
      </button>
    </header>

    <div class="mt-10 grid grid-cols-1 items-start gap-6 lg:mt-14 lg:grid-cols-2 lg:gap-8">
      <!-- Selected plan card -->
      <div class="animate-fade-in-up order-2 overflow-hidden rounded-2xl border-2 bg-white lg:order-1"
           [class]="selectedPlan.theme.selected.replace('-translate-y-1.5', '')"
           style="animation-delay: 80ms">
        <!-- Header band -->
        <div class="relative px-6 pb-7 pt-6 lg:px-7" [class]="selectedPlan.theme.header">
          <span aria-hidden="true"
                class="plan-check absolute right-5 top-5 flex h-7 w-7 items-center justify-center rounded-full border-2"
                [class]="selectedPlan.theme.indicatorOn">
            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" class="h-3.5 w-3.5">
              <path d="M5 10.5l3.2 3.2L15 7" />
            </svg>
          </span>

          <div class="flex min-h-[1.5rem] items-center">
            @if (selectedPlan.badge) {
              <span class="rounded-full px-3 py-1 text-[11px] font-semibold uppercase tracking-wider" [class]="selectedPlan.theme.badge">
                {{ selectedPlan.badge }}
              </span>
            }
          </div>

          <h2 class="mt-3 text-sm font-semibold uppercase tracking-[0.18em]" [class]="selectedPlan.theme.title">
            {{ selectedPlan.name }}
          </h2>
          <p class="mt-3 flex flex-wrap items-baseline gap-x-1.5">
            <span class="text-4xl font-semibold tracking-tight" [class]="selectedPlan.theme.price">
              ₹{{ plan.amount | number }}
            </span>
            <span class="text-sm font-medium" [class]="selectedPlan.theme.muted">/ {{ plan.duration }}</span>
          </p>
          <p class="mt-2 text-sm" [class]="selectedPlan.theme.muted">{{ selectedPlan.tagline }}</p>
        </div>

        <!-- Features: included vs not included in this plan -->
        <ul class="divide-y divide-black/[0.07] px-6 py-3 lg:px-7">
          @for (feature of selectedPlan.features; track feature.label; let i = $index) {
          <li class="feature-row flex items-start gap-3 py-3" [style.animation-delay.ms]="200 + i * 60">
            @if (feature.included) {
              <span class="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#8c52ff] text-white">
                <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" stroke-linejoin="round" class="h-3 w-3" aria-hidden="true">
                  <path d="M5 10.5l3.2 3.2L15 7" />
                </svg>
              </span>
            } @else {
              <span class="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-stone-100 text-black/40">
                <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2.6" stroke-linecap="round" class="h-3 w-3" aria-hidden="true">
                  <path d="M6 6l8 8M14 6l-8 8" />
                </svg>
              </span>
            }
            <span class="min-w-0">
              <span class="block text-sm font-medium" [class]="feature.included ? 'text-[#111111]' : 'text-black/40'">
                {{ feature.label }}
                @if (feature.value && feature.included) {
                  <span class="ml-1 font-semibold text-[#8c52ff]">· {{ feature.value }}</span>
                }
                <span class="sr-only">{{ feature.included ? '(included)' : '(not included)' }}</span>
              </span>
              <span class="mt-0.5 block text-xs" [class]="feature.included ? 'text-black/50' : 'text-black/35'">
                {{ feature.description }}
              </span>
            </span>
          </li>
          }
        </ul>

        <div class="px-6 pb-6 lg:px-7">
          <div class="flex items-center justify-center rounded-full bg-stone-100 py-3 text-sm font-semibold text-[#595959]">
            {{ includedFeatureCount }} of {{ selectedPlan.features.length }} features included
          </div>
        </div>
      </div>

      <!-- Order summary -->
      <div class="animate-fade-in-up order-1 rounded-[28px] border border-black/[0.08] bg-white p-6 shadow-[0_28px_60px_-32px_rgba(0,0,0,0.12)] sm:p-8 lg:sticky lg:top-24 lg:order-2"
           style="animation-delay: 160ms">
        <h3 class="order-summary-title font-sans text-2xl font-medium text-[#111111]">Order summary</h3>

        <!-- Billing Details -->
        <div class="mt-6">
          <h4 class="text-xs font-semibold uppercase tracking-[0.2em] text-black/50">Billing Details</h4>
          <dl class="billing-details mt-3 space-y-2 rounded-2xl bg-[#fffcf9] p-4 text-sm">
            <div class="flex justify-between gap-4">
              <dt class="text-black/50">Name</dt>
              <dd class="font-medium text-[#111111]">{{user.name}}</dd>
            </div>
            <div class="flex justify-between gap-4">
              <dt class="text-black/50">Email</dt>
              <dd class="font-medium text-[#111111]">{{user.email}}</dd>
            </div>
            <div class="flex justify-between gap-4">
              <dt class="text-black/50">Phone</dt>
              <dd class="font-medium text-[#111111]">{{user?.country?.dial}} {{user.phone}}</dd>
            </div>
            <div class="flex justify-between gap-4">
              <dt class="text-black/50">User Type</dt>
              <dd class="font-medium text-[#111111]">{{user.isUserType}}</dd>
            </div>
            <div class="flex items-center justify-between gap-4">
              <dt class="text-black/50">User Status</dt>
              <dd>
                <span class="rounded-full px-2.5 py-1 text-xs font-semibold"
                      [ngClass]="user.isApproved ? 'bg-[#f7f2ff] text-[#8c52ff]' : 'bg-amber-50 text-amber-700'">
                  {{ user.isApproved ? 'Approved' : 'Approval Pending' }}
                </span>
              </dd>
            </div>
          </dl>
        </div>

        <!-- Outside Karnataka -->
        <label class="mt-5 flex cursor-pointer items-center gap-3 rounded-2xl border border-black/10 px-4 py-3 text-sm text-[#595959] transition-colors duration-200 hover:border-[#8c52ff]/40 hover:bg-[#f7f2ff]">
          <input type="checkbox"
                 [(ngModel)]="isOutsideBangalore"
                 class="h-4 w-4 rounded border-stone-300 accent-[#8c52ff]" />
          Residents outside Karnataka
        </label>

        <!-- Pricing Breakdown -->
        <div class="mt-6 space-y-3 text-sm text-[#595959]">
          <div class="flex justify-between">
            <span class="font-semibold text-[#111111]">{{ selectedPlan.name }} Membership</span>
            <span class="font-semibold text-[#111111]">₹{{ plan.amount | number }}</span>
          </div>
          <div class="flex justify-between">
            <span>Subtotal</span>
            <span>₹{{ plan.amount | number }}</span>
          </div>
          <div class="flex justify-between">
            <span>{{ isOutsideBangalore ? 'IGST (18%)' : 'CGST (9%) + SGST (9%)' }}</span>
            <span>₹{{ gstAmount | number:'1.0-0' }}</span>
          </div>
        </div>

        <!-- Total -->
        <div class="mt-5 flex items-baseline justify-between border-t border-dashed border-black/10 pt-5">
          <span class="text-base font-semibold text-[#111111]">Total</span>
          <span class="text-2xl font-semibold tracking-tight text-[#8c52ff]">₹{{ totalAmount | number:'1.0-0' }}</span>
        </div>

        <!-- CTA -->
        <button type="button"
                (click)="makePayment()"
                [disabled]="isProcessing"
                class="group mt-6 inline-flex w-full items-center justify-center gap-2 rounded-full theme-btn1 bg-[#8c52ff] px-8 py-4 text-base font-semibold text-white shadow-sm transition-all duration-200 hover:-translate-y-0.5 hover:bg-[#7439e6] hover:shadow-xl focus:outline-none focus-visible:ring-4 focus-visible:ring-[#8c52ff]/30 active:translate-y-0 active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-70 disabled:hover:translate-y-0">
          @if (isProcessing) {
            <span class="h-4 w-4 animate-spin rounded-full border-2 border-white/40 border-t-white" aria-hidden="true"></span>
            Processing…
          } @else {
            Pay ₹{{ totalAmount | number:'1.0-0' }}
            <svg viewBox="0 0 20 20" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
                 class="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" aria-hidden="true">
              <path d="M4 10h12M11 5l5 5-5 5" />
            </svg>
          }
        </button>

        <p class="mt-4 text-center text-sm text-black/50">
          No cancellations once the subscription is activated.
        </p>
        <p class="mt-2 text-center text-xs text-black/40">
          Secure checkout with Cashfree
        </p>
      </div>
    </div>
  </div>
</section>
}
